import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Button, Chip, CircularProgress, IconButton, Paper, Popover, Stack,
  TextField, ToggleButton, ToggleButtonGroup, Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SaveIcon from '@mui/icons-material/Save';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import iziToast from 'izitoast';
import { Link, useParams } from 'react-router-dom';

import JournalLayout from '../../components/Journal/JournalLayout';
import { PERMISSIONS, resetCurrentUser, useCurrentUser } from '../../utils/currentUser';
import { deleteMark, getMarkHistory, getSheet, saveMarks } from '../../actions/journal';
import { internalServerError } from '../../utils/consts/pathRoutes';
import {
  currentMonth, formatDate, formatMonth, initials, shiftMonth, studentCardLink, studentName, weekdayShort,
} from '../../utils/journalFormat';
import StudentHistoryTooltip from '../../components/Journal/StudentHistoryTooltip';
import MyColorPicker from '../../components/Journal/MyColorPicker';
import useIsMobile from '../../utils/useIsMobile';
import LessonModal from './LessonModal';
import './sheet.css';

const MARK_COLORS = {
  '0': '#ffcdd2',   // без уважительной причины — красный
  'У': '#ffe0b2',   // уважительная — оранжевый
  'С': '#d1c4e9',   // медсправка — сиреневый
};

// Ориентир заказчика — 0–10 часов в день. Ввод не блокируем, только предупреждаем.
const HOURS_HINT_LIMIT = 10;

/**
 * Лист журнала: даты по горизонтали, слушатели по вертикали.
 *
 * Колонка — это занятие, а не день: в одном дне может быть несколько пар от разных
 * преподавателей, и часы у них считаются отдельно. Дни объединяются в шапке.
 *
 * Отметки копятся в состоянии и уходят одной пачкой — на сервере одно ядро,
 * запрос на каждое нажатие клавиши ему не нужен.
 */
const SheetPage = () => {
  const { id } = useParams();
  const { user, can } = useCurrentUser();
  const canWrite = can(PERMISSIONS.JOURNAL_MARK_WRITE);
  const myColor = (user?.groups || []).find((group) => String(group.id) === String(id))?.color;
  const isMobile = useIsMobile();

  const [month, setMonth] = useState(currentMonth());
  const [sheet, setSheet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState({});          // ключ `${lessonId}:${studentId}` → отметка
  const [editor, setEditor] = useState(null);      // редактор отметки
  const [lessonModal, setLessonModal] = useState(null); // { mode: 'create' | 'edit', lesson }

  // Через него находим ячейку по data-cell при переходах с клавиатуры.
  const tableRef = useRef(null);

  const load = () => {
    setLoading(true);
    // Сбрасываем прежний лист: иначе при переходе в другую группу до ответа сервера
    // в шапке остаются имя и куратор предыдущей — выглядит как «везде один куратор».
    setSheet(null);
    getSheet(id, month)
      .then((data) => { setSheet(data); setDirty({}); })
      .catch((e) => iziToast.error({ message: internalServerError(e), position: 'topRight' }))
      .finally(() => setLoading(false));
  };

  useEffect(load, [id, month]);

  // Отметки: сохранённые с сервера, поверх — несохранённые правки.
  const marks = useMemo(() => {
    const map = {};
    (sheet?.marks || []).forEach((mark) => {
      map[`${mark.lesson_id}:${mark.student_id}`] = mark;
    });
    return { ...map, ...dirty };
  }, [sheet, dirty]);

  // Занятия, сгруппированные по дате — для двухэтажной шапки.
  const days = useMemo(() => {
    const grouped = [];
    (sheet?.lessons || []).forEach((lesson) => {
      const last = grouped[grouped.length - 1];
      if (last && last.date === lesson.lesson_date) last.lessons.push(lesson);
      else grouped.push({ date: lesson.lesson_date, lessons: [lesson] });
    });
    return grouped;
  }, [sheet]);

  const dirtyCount = Object.keys(dirty).length;

  const totalsByStudent = useMemo(() => {
    const totals = {};
    (sheet?.students || []).forEach((student) => {
      totals[student.student_id] = (sheet.lessons || []).reduce((sum, lesson) => {
        const mark = marks[`${lesson.id}:${student.student_id}`];
        return sum + (mark && mark.mark_type === 'Часы' ? Number(mark.hours) : 0);
      }, 0);
    });
    return totals;
  }, [sheet, marks]);

  const openEditor = (anchor, lessonId, studentId) => {
    const existing = marks[`${lessonId}:${studentId}`];

    // Отсутствие без причины — это ноль часов. Отдельной кнопки «0» больше нет,
    // поэтому старые отметки этого типа открываем как часы, равные нулю.
    const isZero = existing?.mark_type === '0';

    setEditor({
      anchor,
      lessonId,
      studentId,
      // На сервере отметка или только в несохранённых правках: от этого зависит,
      // нужно ли при очистке обращаться к серверу.
      saved: (sheet?.marks || []).some(
        (mark) => mark.lesson_id === lessonId && mark.student_id === studentId
      ),
      exists: Boolean(existing),
      history: null,
      value: {
        mark_type: isZero ? 'Часы' : (existing?.mark_type || 'Часы'),
        hours: isZero ? 0 : (existing?.hours ?? ''),
        comment: existing?.comment || '',
      },
    });

    // История подгружается отдельно: у большинства ячеек правок не было,
    // и тянуть её вместе с листом — лишние строки на каждую из сотен ячеек.
    if (existing) {
      getMarkHistory(id, lessonId, studentId)
        .then((rows) => setEditor((prev) => (
          prev && prev.lessonId === lessonId && prev.studentId === studentId
            ? { ...prev, history: rows }
            : prev
        )))
        .catch(() => {});
    }
  };

  const applyEditor = () => {
    const { lessonId, studentId, value } = editor;
    const key = `${lessonId}:${studentId}`;

    if (value.mark_type === 'Часы' && String(value.hours).trim() === '') return;

    const hours = value.mark_type === 'Часы' ? Number(value.hours) : 0;

    // Ноль часов и есть «отсутствовал без причины»: в базе это тип «0», по нему считаются
    // пропуски в статистике и уведомлениях. Отдельной кнопки для него в интерфейсе нет.
    const markType = value.mark_type === 'Часы' && hours === 0 ? '0' : value.mark_type;
    const comment = value.comment || null;

    // Ячейку открыли посмотреть и закрыли по «Ок» — счётчик несохранённого расти не должен.
    // Сравниваем с тем, что лежит на сервере: если совпало, заодно снимаем прежнюю правку,
    // когда значение поменяли и вернули обратно.
    const saved = (sheet?.marks || []).find(
      (mark) => mark.lesson_id === lessonId && mark.student_id === studentId
    );

    const unchanged = saved
      ? saved.mark_type === markType
        && Number(saved.hours) === hours
        && (saved.comment || null) === comment
      : false;

    setDirty((prev) => {
      const next = { ...prev };

      if (unchanged) delete next[key];
      else next[key] = { lesson_id: lessonId, student_id: studentId, mark_type: markType, hours, comment };

      return next;
    });

    setEditor(null);
  };

  /**
   * Очистка ячейки.
   *
   * Раньше отметку нельзя было убрать: можно было поставить ноль или другой статус,
   * но не вернуть пустую ячейку. Несохранённую правку просто забываем, сохранённую
   * удаляем на сервере — там же пишется история изменения.
   */
  const clearEditor = () => {
    const { lessonId, studentId, saved } = editor;
    const key = `${lessonId}:${studentId}`;

    setDirty((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });

    setEditor(null);

    if (!saved) return;

    deleteMark(id, lessonId, studentId)
      .then(() => {
        iziToast.success({ message: 'Отметка убрана', position: 'topRight' });

        // Лист целиком не перезагружаем: load() сбрасывает несохранённые правки,
        // и очистка одной ячейки стирала бы всю остальную незаписанную работу.
        setSheet((prev) => (prev ? {
          ...prev,
          marks: prev.marks.filter(
            (mark) => !(mark.lesson_id === lessonId && mark.student_id === studentId)
          ),
        } : prev));
      })
      .catch((e) => iziToast.error({ message: internalServerError(e), position: 'topRight' }));
  };

  const persist = () => {
    setSaving(true);
    saveMarks(id, Object.values(dirty))
      .then((result) => {
        iziToast.success({ message: `Сохранено отметок: ${result.saved}`, position: 'topRight' });
        load();
      })
      .catch((e) => iziToast.error({ message: internalServerError(e), position: 'topRight' }))
      .finally(() => setSaving(false));
  };

  /**
   * Перемещение по ячейкам с клавиатуры.
   *
   * Журнал заполняют колонками, по одному занятию за раз, поэтому Tab ведёт вниз по
   * слушателям, а не вправо по датам: дойдя до конца колонки, он переходит в начало
   * следующей — то есть к следующей паре, а если день закончился, к первой паре
   * следующего дня. Стрелки двигают в свою сторону и за границы не выходят.
   *
   * Адресуем ячейки через data-атрибут: держать ref на каждую из сотен ячеек дороже,
   * чем один раз спросить у таблицы нужную.
   */
  const cellKey = (lesson, student) => `${lesson.id}:${student.student_id}`;

  /**
   * Первая ячейка, принимающая фокус, начиная с (lessonIndex, studentIndex).
   *
   * Шаг задаётся по каждой оси отдельно: ячейки вне периода обучения фокус не принимают,
   * и пропускать их нужно по той же оси, по которой шли. Иначе стрелка вправо, наткнувшись
   * на выбывшего слушателя, уводила курсор в чужую строку.
   *
   * wrap включается только для Tab: он один переходит из конца колонки в начало следующей.
   */
  const findCell = (lessonIndex, studentIndex, step, { wrap = false } = {}) => {
    const lessons = sheet?.lessons || [];
    const students = sheet?.students || [];

    let li = lessonIndex;
    let si = studentIndex;

    for (let guard = 0; guard <= lessons.length * students.length; guard += 1) {
      if (li < 0 || li >= lessons.length) return null;

      if (si < 0 || si >= students.length) {
        if (!wrap) return null;

        if (si >= students.length) { li += 1; si = 0; } else { li -= 1; si = students.length - 1; }
        continue;
      }

      const target = tableRef.current?.querySelector(
        `[data-cell="${cellKey(lessons[li], students[si])}"]`
      );

      if (target) return target;

      li += step.lesson;
      si += step.student;
    }

    return null;
  };

  const onCellKeyDown = (event, lessonIndex, studentIndex) => {
    const lessons = sheet?.lessons || [];
    const students = sheet?.students || [];

    if (event.key === 'Enter') {
      event.preventDefault();
      if (canWrite) {
        openEditor(event.currentTarget, lessons[lessonIndex].id, students[studentIndex].student_id);
      }
      return;
    }

    // Стрелки: шаг по своей оси, без перехода в соседнюю колонку или строку.
    const arrows = {
      ArrowUp: [lessonIndex, studentIndex - 1, { lesson: 0, student: -1 }],
      ArrowDown: [lessonIndex, studentIndex + 1, { lesson: 0, student: 1 }],
      ArrowLeft: [lessonIndex - 1, studentIndex, { lesson: -1, student: 0 }],
      ArrowRight: [lessonIndex + 1, studentIndex, { lesson: 1, student: 0 }],
    };

    if (arrows[event.key]) {
      // preventDefault даже когда двигаться некуда: иначе стрелка прокрутит страницу.
      event.preventDefault();
      findCell(...arrows[event.key])?.focus();
      return;
    }

    if (event.key === 'Tab') {
      const step = { lesson: 0, student: event.shiftKey ? -1 : 1 };
      const next = findCell(
        lessonIndex,
        studentIndex + step.student,
        step,
        { wrap: true },
      );

      // На краях таблицы отдаём Tab браузеру: иначе с первой и последней ячейки
      // не выйти на кнопки над журналом.
      if (!next) return;

      event.preventDefault();
      next.focus();
    }
  };

  const renderCell = (lesson, student, lessonIndex, studentIndex) => {
    const key = `${lesson.id}:${student.student_id}`;
    const mark = marks[key];
    const isDirty = Boolean(dirty[key]);

    // Слушатель ещё не поступил или уже выбыл — ячейка вне его периода.
    const outside = lesson.lesson_date < student.date_from
      || (student.date_to && lesson.lesson_date > student.date_to);

    if (outside) {
      return <td key={key} className="sheet__cell sheet__cell--outside">—</td>;
    }

    return (
      <td
        key={key}
        data-cell={key}
        tabIndex={0}
        onKeyDown={(event) => onCellKeyDown(event, lessonIndex, studentIndex)}
        className={`sheet__cell${isDirty ? ' sheet__cell--dirty' : ''}${canWrite ? '' : ' sheet__cell--readonly'}`}
        style={{
          background: mark ? MARK_COLORS[mark.mark_type] : undefined,
          // Часы окрашиваются цветом преподавателя, который вёл эту пару — так в исходном
          // листе видно, кто выдал часы. У отметок об отсутствии свой цвет по легенде.
          boxShadow: mark && mark.mark_type === 'Часы' && lesson.teacher_color
            ? `inset 3px 0 0 ${lesson.teacher_color}`
            : undefined,
        }}
        onClick={canWrite
          ? (event) => openEditor(event.currentTarget, lesson.id, student.student_id)
          : undefined}
      >
        {mark ? (mark.mark_type === 'Часы' ? mark.hours : mark.mark_type) : ''}
        {mark?.comment && <ChatBubbleIcon className="sheet__comment-icon" />}
      </td>
    );
  };

  // На телефоне шапка листа — в три строки: название с куратором, месяц, кнопки.
  const wrapOnMobile = isMobile ? { spacing: 0, sx: { flexWrap: 'wrap', gap: 1 } } : {};

  // На узком экране окно редактора перекрывает закреплённый столбец с фамилиями
  // и шапку с датами, поэтому в нём подписано, чья это отметка и за какое занятие.
  const editorStudent = isMobile && editor
    ? (sheet?.students || []).find((student) => student.student_id === editor.studentId)
    : null;
  const editorLesson = isMobile && editor
    ? (sheet?.lessons || []).find((lesson) => lesson.id === editor.lessonId)
    : null;

  const saveButton = (
    <Button
      variant="contained" startIcon={<SaveIcon />}
      disabled={dirtyCount === 0 || saving} onClick={persist}
    >
      Сохранить{dirtyCount > 0 ? ` (${dirtyCount})` : ''}
    </Button>
  );

  return (
    <JournalLayout activeTab="sheet">
      <Box>
        <Stack
          direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}
          {...(isMobile && { direction: 'column', alignItems: 'flex-start', spacing: 1 })}
        >
          <Stack direction="row" alignItems="center" spacing={1} {...wrapOnMobile}>
            <h2 className="journal-title">
              Журнал {sheet?.group?.name || ''}
            </h2>

            {sheet?.group?.curator && (
              <Chip size="small" variant="outlined"
                label={`Куратор: ${sheet.group.curator.name}`} />
            )}

            {/* На телефоне этот отступ становится переносом: месяц — отдельной строкой. */}
            <Box sx={isMobile ? { flexBasis: '100%', height: 0 } : { width: 16 }} />

            <IconButton size="small" onClick={() => setMonth(shiftMonth(month, -1))}>
              <ChevronLeftIcon />
            </IconButton>
            <span className="sheet__month">{formatMonth(month)}</span>
            <IconButton size="small" onClick={() => setMonth(shiftMonth(month, 1))}>
              <ChevronRightIcon />
            </IconButton>
          </Stack>

          {canWrite && (
            <Stack direction="row" spacing={1} alignItems="center" {...wrapOnMobile}>
              {myColor !== undefined && (
                <MyColorPicker
                  color={myColor}
                  onChanged={() => {
                    // В /me лежит прежний цвет — сбрасываем кэш, иначе образец
                    // на кнопке останется старым до перезагрузки вкладки.
                    resetCurrentUser();
                    load();
                  }}
                />
              )}
              <Button startIcon={<AddIcon />} onClick={() => setLessonModal({ mode: 'create' })}>
                Занятие
              </Button>
              {/* На телефоне «Сохранить» живёт в нижней панели: до кнопки наверху
                  от последних строк листа приходится долго листать. */}
              {!isMobile && saveButton}
            </Stack>
          )}
        </Stack>

        {sheet?.teachers?.length > 0 && (
          <Stack
            direction="row" spacing={2} alignItems="center"
            sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}
            className="sheet__legend"
          >
            <span className="journal-muted">Преподаватели:</span>

            {sheet.teachers.map((teacher) => (
              <Tooltip
                key={teacher.id}
                title={`${teacher.name}: ${teacher.hours} ч за ${formatMonth(month).toLowerCase()}`
                  + `${teacher.is_curator ? ' · куратор группы' : ''}`
                  + `${teacher.is_hourly ? ' · почасовик' : ''}`}
              >
                <span className="sheet__legend-item">
                  <i
                    className="sheet__legend-color"
                    style={{ background: teacher.color || '#d6d6d6' }}
                  />
                  {teacher.name}
                  <b> {teacher.hours} ч</b>
                  {teacher.is_curator && <span className="sheet__legend-role">куратор</span>}
                  {teacher.is_hourly && <span className="sheet__legend-role">почасовик</span>}
                </span>
              </Tooltip>
            ))}
          </Stack>
        )}

        {loading ? (
          <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress color="warning" /></Stack>
        ) : !sheet?.lessons?.length ? (
          <Paper sx={{ p: 4, textAlign: 'center' }} className="journal-muted">
            За {formatMonth(month)} занятий нет. Добавьте занятие, чтобы начать вести журнал.
          </Paper>
        ) : (
          <Paper className="sheet__wrapper">
            <table className="sheet" ref={tableRef}>
              <thead>
                <tr>
                  <th className="sheet__name sheet__name--head" rowSpan={2}>Слушатель</th>
                  {days.map((day) => (
                    <th key={day.date} colSpan={day.lessons.length} className="sheet__day">
                      <div>{formatDate(day.date)}</div>
                      <div className="sheet__weekday">
                        {weekdayShort(day.date)} · {sheet.planned_by_date[day.date]} ч
                      </div>
                    </th>
                  ))}
                  <th className="sheet__total" rowSpan={2}>Итого</th>
                </tr>
                <tr>
                  {days.flatMap((day) => day.lessons.map((lesson) => (
                    <th
                      key={lesson.id}
                      className="sheet__slot"
                      style={{ borderBottom: lesson.teacher_color ? `3px solid ${lesson.teacher_color}` : undefined }}
                      onClick={canWrite ? () => setLessonModal({ mode: 'edit', lesson }) : undefined}
                    >
                      <Tooltip
                        title={`${lesson.planned_hours} ч · ${lesson.teacher_name || 'преподаватель не указан'}`
                          + `${lesson.is_restored ? ' · восстановленная пара' : ''} — нажмите, чтобы изменить`}
                      >
                        <span>
                          {lesson.planned_hours}ч
                          {lesson.teacher_name && (
                            <span className="sheet__teacher"> {initials(lesson.teacher_name)}</span>
                          )}
                          {lesson.is_restored && ' ↻'}
                        </span>
                      </Tooltip>
                    </th>
                  )))}
                </tr>
              </thead>

              <tbody>
                {sheet.students.map((student, studentIndex) => (
                  <tr key={student.membership_id} className={student.is_active ? '' : 'sheet__row--former'}>
                    <td className="sheet__name">
                      <StudentHistoryTooltip studentId={student.student_id}>
                        <Link className="sheet__student" to={studentCardLink(student)}>
                          {studentName(student)}
                        </Link>
                      </StudentHistoryTooltip>
                      <div className="sheet__country">
                        {student.country}
                        {student.hours_carried_over > 0 && ` · принесено ${student.hours_carried_over} ч`}
                      </div>
                    </td>

                    {(sheet.lessons || []).map((lesson, lessonIndex) => (
                      renderCell(lesson, student, lessonIndex, studentIndex)
                    ))}

                    <td className="sheet__total">{totalsByStudent[student.student_id]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Paper>
        )}

        {isMobile && canWrite && (
          <div className="sheet__mobile-save">
            <span className="journal-muted">
              {dirtyCount > 0 ? `Не сохранено: ${dirtyCount}` : 'Все отметки сохранены'}
            </span>
            {saveButton}
          </div>
        )}
      </Box>

      <Popover
        open={Boolean(editor)}
        anchorEl={editor?.anchor}
        onClose={() => setEditor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        /**
         * Enter подтверждает отметку — иначе за каждый день по каждому слушателю
         * приходится тянуться мышкой к «Ок», а журнал заполняют колонками.
         *
         * Обработчик на самом окне, а не на его содержимом: когда отметка не «Часы»,
         * поля ввода нет и фокус остаётся на самом окне — события с него до вложенных
         * элементов не доходят.
         *
         * В комментарии Enter переносит строку, там подтверждает Ctrl+Enter.
         * Переключатели «Часы / 0 / У / С» реагируют на пробел, так что клавиатурный
         * выбор не потерян.
         */
        onKeyDown={(event) => {
          if (!editor || event.key !== 'Enter') return;

          // Фокус на переключателе «Часы / У / С» — Enter выбирает его, это делает сама
          // кнопка. Подтверждать отметку в этот момент было бы неожиданно: человек
          // дошёл табом до нужного статуса и ещё не ввёл часы.
          if (event.target.closest('.MuiToggleButtonGroup-root')) return;

          const isComment = event.target.tagName === 'TEXTAREA';
          if (isComment && !event.ctrlKey && !event.metaKey) return;

          event.preventDefault();
          applyEditor();
        }}
      >
        {editor && (
          <Stack spacing={2} sx={{ p: 2, width: 260 }}>
            {editorStudent && editorLesson && (
              <div className="journal-muted" style={{ lineHeight: 1.4 }}>
                <b style={{ color: '#2f2f2f' }}>{studentName(editorStudent)}</b>
                <br />
                {formatDate(editorLesson.lesson_date)} · {editorLesson.planned_hours} ч
                {editorLesson.teacher_name ? ` · ${editorLesson.teacher_name}` : ''}
              </div>
            )}

            <ToggleButtonGroup
              size="small" exclusive fullWidth value={editor.value.mark_type}
              onChange={(event, value) => value && setEditor((prev) => ({
                ...prev, value: { ...prev.value, mark_type: value },
              }))}
            >
              <ToggleButton value="Часы">Часы</ToggleButton>
              <ToggleButton value="У">У</ToggleButton>
              <ToggleButton value="С">С</ToggleButton>
            </ToggleButtonGroup>

            {/* На телефоне без автофокуса: клавиатура, выскочившая сразу, прокручивает
                страницу к полю и уводит окно от ячейки. Открывается касанием поля. */}
            {editor.value.mark_type === 'Часы' && (
              <TextField
                autoFocus={!isMobile} size="small" type="number" label="Часов"
                inputProps={isMobile ? { min: 0, max: 100, inputMode: 'decimal' } : { min: 0, max: 100 }}
                value={editor.value.hours}
                onChange={(event) => setEditor((prev) => ({
                  ...prev, value: { ...prev.value, hours: event.target.value },
                }))}
                helperText={Number(editor.value.hours) > HOURS_HINT_LIMIT
                  ? `Больше ${HOURS_HINT_LIMIT} часов за день — проверьте`
                  : ' '}
                FormHelperTextProps={{
                  sx: { color: Number(editor.value.hours) > HOURS_HINT_LIMIT ? 'warning.main' : undefined },
                }}
              />
            )}

            <TextField
              size="small" label="Комментарий" multiline rows={2}
              value={editor.value.comment}
              onChange={(event) => setEditor((prev) => ({
                ...prev, value: { ...prev.value, comment: event.target.value },
              }))}
            />

            {editor.history?.length > 0 && (
              <div className="sheet__history">
                <div className="journal-muted" style={{ marginBottom: 4 }}>История правок</div>
                {editor.history.slice(0, 4).map((row) => (
                  <div key={row.id}>
                    {row.old_mark_type
                      ? `${row.old_mark_type}${row.old_mark_type === 'Часы' ? ` ${row.old_hours}` : ''} → `
                      : ''}
                    {row.new_mark_type
                      ? `${row.new_mark_type}${row.new_mark_type === 'Часы' ? ` ${row.new_hours}` : ''}`
                      : 'удалена'}
                    <span className="journal-muted">
                      {' '}· {row.changed_by_name || '—'}, {formatDate(String(row.changed_at).slice(0, 10))}
                    </span>
                  </div>
                ))}
              </div>
            )}

            <Stack direction="row" spacing={1}>
              {editor.exists && (
                <Button size="small" color="error" onClick={clearEditor}>Очистить</Button>
              )}
              <Button size="small" onClick={() => setEditor(null)} fullWidth>Отмена</Button>
              <Button
                size="small" variant="contained" onClick={applyEditor} fullWidth
                disabled={editor.value.mark_type === 'Часы' && String(editor.value.hours).trim() === ''}
              >
                Ок
              </Button>
            </Stack>
          </Stack>
        )}
      </Popover>

      <LessonModal
        state={lessonModal} groupId={id} month={month}
        onClose={() => setLessonModal(null)}
        onSaved={() => { setLessonModal(null); load(); }}
      />
    </JournalLayout>
  );
};

export default SheetPage;
